import { Group, Menu } from '@mantine/core';
import { useDisclosure } from '@mantine/hooks';
import { notifications } from '@mantine/notifications';
import type { PolicyOut } from '@repo/api-codegen';
import {
	apiQueryKey,
	useApiDeletePolicy,
	useApiRunPolicy,
} from '@repo/api-codegen';
import { trackEvent } from '@repo/common/analytics';
import { EntityType } from '@repo/common/enums/entityType';
import { Breadcrumbs, Button, Icon, IconButton } from '@repo/foundations';
import { isNil } from 'lodash-es';
import { observer } from 'mobx-react-lite';
import { useCallback, useMemo, useState } from 'react';
import { useNavigate } from 'react-router';
import { queryClient, useAuthUser, useWorkspace } from '../../api';
import { useBackgroundJob } from '../../api/hooks/backgroundJob';
import {
	policiesQueryKeyFactory,
	useListPolicyRules,
	usePolicyWritePermission,
} from '../../api/hooks/policy';
import ActivityLogDrawer from '../../components/ActivityLog/ActivityLogDrawer';
import { AIAssistantSidebarToggle } from '../../components/AIAssistant';
import { ActivityLogAction } from '../../components/EntityPageLayout/EntityActions/Actions/ActivityLogAction';
import { CopyLinkAction } from '../../components/EntityPageLayout/EntityActions/Actions/CopyLinkAction';
import { EntityPageSidebarToggle } from '../../components/EntityPageSidebarToggle';
import { openDeleteConfirmModal } from '../../components/ModalManager';
import {
	NavBarBreadcrumbWrapper,
	NavBarRightSideWrapper,
	NavBarSidesheetControlsWrapper,
	NavBarWrapper,
} from '../../components/SecodaAppShell/SecodaNavBar';
import { useFeatureFlags } from '../../utils/featureFlags';
import { useTogglePolicyEnable } from '../PoliciesPage/hooks';
import { PolicyToggle } from './PolicyToggle';

interface PolicyPageNavBarProps {
	policy: PolicyOut;
}

const PolicyPageNavBar = observer(({ policy }: PolicyPageNavBarProps) => {
	const { user } = useAuthUser();
	const { workspace } = useWorkspace();
	const navigate = useNavigate();
	const hasWritePermission = usePolicyWritePermission();
	const { dataScanning } = useFeatureFlags();
	const { mutateAsync: runPolicy } = useApiRunPolicy({});
	const [backgroundJobId, setBackgroundJobId] = useState<string | null>(null);
	const [isRunning, setIsRunning] = useState(false);
	const { data: policyRules } = useListPolicyRules(
		{ pathParams: { policyId: policy.id } },
		{ enabled: !!policy.id }
	);
	const displayRunScanButton = useMemo(
		() =>
			dataScanning &&
			hasWritePermission &&
			policyRules?.results.some((rule) => rule.type === 'PII_SCAN'),
		[dataScanning, hasWritePermission, policyRules]
	);

	const crumbs = [
		{
			title: 'Policies',
			href: '/policies',
		},
		{
			title: policy.name,
			href: `/policy/${policy.id}`,
		},
	];

	const { mutateAsync: togglePolicy } = useTogglePolicyEnable({});
	const toggleCallBack = useCallback(
		(record: PolicyOut, enabled: boolean) => {
			togglePolicy({
				body: {
					enabled: enabled,
				},
				pathParams: {
					policyId: record.id,
				},
			});
		},
		[togglePolicy]
	);

	const [logDrawerOpen, { open: openLogDrawer, close: closeLogDrawer }] =
		useDisclosure(false);

	const { mutateAsync: deletePolicy } = useApiDeletePolicy({});
	const handleRunScan = useCallback(async () => {
		const { id } = await runPolicy({
			body: {
				policy_id: policy.id,
			},
		});
		setBackgroundJobId(id);
		setIsRunning(true);
	}, [runPolicy, policy.id]);

	useBackgroundJob({
		id: backgroundJobId || '',
		options: {
			enabled: !isNil(backgroundJobId),
			refetchInterval: (query) => {
				const { data } = query.state;

				if (data?.failed || data?.completed) {
					setIsRunning(false);
					queryClient.invalidateQueries({
						queryKey: apiQueryKey('policy/{policy_id}', {
							policy_id: policy.id,
						}),
					});
					queryClient.invalidateQueries({
						queryKey: apiQueryKey('policy/{policy_id}/results', {
							policy_id: policy.id,
						}),
					});
					return false;
				}
				return 1000;
			},
		},
	});

	const handleDeleteClick = useCallback(() => {
		openDeleteConfirmModal({
			title: `Delete Policy?`,
			description: "You can't undo this action.",
			onConfirm: async () => {
				const notificationId = `delete-policy-${policy.id}`;
				notifications.show({
					id: notificationId,
					loading: true,
					message: `Deleting ${policy.name}...`,
				});

				try {
					await deletePolicy({ pathParams: { policyId: policy.id } });

					trackEvent(
						`/delete`,
						{
							id: policy.id,
						},
						user,
						workspace
					);

					notifications.update({
						id: notificationId,
						message: `Successfully deleted ${policy.name}`,
					});

					queryClient.invalidateQueries({
						queryKey: policiesQueryKeyFactory.all(),
					});

					navigate('/policies');
				} catch (error) {
					notifications.update({
						id: notificationId,
						message: `Failed to delete ${policy.name}`,
						color: 'red',
					});
				}
			},
		});
	}, [deletePolicy, policy.id, policy.name, user, workspace, navigate]);

	return (
		<>
			<NavBarWrapper>
				<NavBarBreadcrumbWrapper>
					<Breadcrumbs crumbs={crumbs} />
				</NavBarBreadcrumbWrapper>
				<NavBarRightSideWrapper>
					<Group spacing="xs">
						<Group spacing="3xs">
							<PolicyToggle
								enabled={policy.enabled}
								onChange={(enabled) => toggleCallBack(policy, enabled)}
								disabled={!hasWritePermission}
							/>
							<Menu
								position="bottom-end"
								width={150}
								closeOnItemClick
								withinPortal
							>
								<Menu.Target>
									<IconButton
										data-testid="policy-actions-menu"
										iconName="dots"
										variant="tertiary"
										tooltip="More actions"
									/>
								</Menu.Target>
								<Menu.Dropdown>
									<CopyLinkAction />
									<Menu.Divider />
									{/* TODO[tan-policies]: Add snapshots action */}
									{/* <SnapshotsAction entity={policy} /> */}
									<ActivityLogAction
										entityType={EntityType.policy}
										openLogDrawer={openLogDrawer}
									/>
									{hasWritePermission && (
										<>
											<Menu.Divider />
											<Menu.Item
												icon={<Icon name="trash" />}
												onClick={handleDeleteClick}
											>
												Delete...
											</Menu.Item>
										</>
									)}
								</Menu.Dropdown>
							</Menu>
						</Group>
						{hasWritePermission && (
							<Button
								onClick={() =>
									navigate(`/policy/${policy.id}/edit`, {
										state: { prev_url_path: `/policy/${policy.id}` },
									})
								}
							>
								Edit policy
							</Button>
						)}
						{displayRunScanButton && (
							<Button
								variant="primary"
								onClick={handleRunScan}
								loading={isRunning}
							>
								Run scan
							</Button>
						)}
					</Group>
				</NavBarRightSideWrapper>
				<NavBarSidesheetControlsWrapper>
					<AIAssistantSidebarToggle />
					<EntityPageSidebarToggle />
				</NavBarSidesheetControlsWrapper>
			</NavBarWrapper>
			<ActivityLogDrawer
				entity={{
					id: policy.id,
					entity_type: EntityType.policy,
					created_at: policy.created_at,
				}}
				open={logDrawerOpen}
				onClose={closeLogDrawer}
			/>
		</>
	);
});

PolicyPageNavBar.displayName = 'PolicyPageNavBar';

export default PolicyPageNavBar;
